import { format, isAfter, parseISO, startOfDay } from 'date-fns';
import type { PaymentAccountResourceModel } from 'api/models';
import type {
  ConsumerFailedPaymentModel,
  ConsumerPaymentMethodSummaryModel,
} from './failed-payments.types';

export function formatShortDate(value?: string | null): string {
  if (!value) return '';
  const date = parseISO(value);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'M/d');
}

export function getFailedPaymentsTotal(
  items: ConsumerFailedPaymentModel[],
): number {
  return items.reduce((sum, item) => sum + (item.amount ?? 0), 0);
}

export function getFailedPaymentsTotalForBalanceIds(
  items: ConsumerFailedPaymentModel[],
  balanceUniqueIds: string[],
): number {
  const selectedBalanceUniqueIds = new Set(balanceUniqueIds);

  return items.reduce((sum, item) => {
    const includesSelectedBalance = getBalanceUniqueIds(item).some(
      (balanceUniqueId) => selectedBalanceUniqueIds.has(balanceUniqueId),
    );

    return includesSelectedBalance ? sum + (item.amount ?? 0) : sum;
  }, 0);
}

// Count the rows the resident sees (one per backend group), not the underlying balances. The backend
// groups dependent balances (e.g. a late fee under its origin rent) into a single failed payment, so a
// rent + its late fee is one payment in the banner/header, not two.
export function getFailedPaymentsCount(
  items: ConsumerFailedPaymentModel[],
): number {
  return items.filter((item) => getBalanceUniqueIds(item).length > 0).length;
}

const STATE_AND_ZIP_PATTERN = /^[A-Za-z]{2}\s+\d{5}(?:-\d{4})?$/;

export function formatAddressWithoutStateAndZip(
  address?: string | null,
): string | null {
  if (!address) return null;

  const parts = address
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (
    parts.length > 1 &&
    STATE_AND_ZIP_PATTERN.test(parts[parts.length - 1] ?? '')
  ) {
    parts.pop();
  }

  return parts.join(', ') || null;
}

export function formatStreetAddress(address?: string | null): string {
  if (!address) return '';
  return address.split(',')[0]?.trim() ?? '';
}

export function shouldUseHomeownerMultiHomeFlow(
  audienceKey: 'resident' | 'homeowner',
  homeGroupCount: number,
): boolean {
  return audienceKey === 'homeowner' && homeGroupCount > 1;
}

const USD_FORMATTER_NO_DECIMALS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const USD_FORMATTER_WITH_DECIMALS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatFailedPaymentsAmount(amount: number): string {
  const formatter = Number.isInteger(amount)
    ? USD_FORMATTER_NO_DECIMALS
    : USD_FORMATTER_WITH_DECIMALS;
  return formatter.format(amount);
}

export function formatNegativeFailedPaymentsAmount(amount: number): string {
  return `-${formatFailedPaymentsAmount(Math.abs(amount))}`;
}

// Payment Summary breakdown rows always show cents (e.g. "-$1,030.00"), matching the Figma redesign,
// unlike the failed-balance list above which omits cents for whole-dollar amounts.
export function formatNegativeFailedPaymentsAmountWithCents(
  amount: number,
): string {
  return `-${USD_FORMATTER_WITH_DECIMALS.format(Math.abs(amount))}`;
}

export function formatLateFee(fee: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: fee % 1 === 0 ? 0 : 2,
  }).format(fee);
}

export function mapPaymentAccountToPaymentMethodSummary(
  paymentAccount: PaymentAccountResourceModel | null | undefined,
): ConsumerPaymentMethodSummaryModel | null {
  if (!paymentAccount?.accountId) return null;

  const provider =
    paymentAccount.achDetails?.bankName ??
    paymentAccount.creditCardDetails?.cardType ??
    'Payment Method';

  return {
    paymentAccountUniqueId: paymentAccount.accountId,
    paymentMethod: paymentAccount.paymentMethod,
    provider,
    lastDigits: paymentAccount.lastDigits,
  };
}

export function getFailedPaymentErrorMessage(
  error: unknown,
  fallback: string,
): string {
  const err = error as
    | {
        data?: Array<{ message?: string }>;
        errors?: Array<{ message?: string }>;
        message?: string;
        title?: string;
      }
    | Array<{ message?: string }>
    | undefined;

  if (Array.isArray(err)) {
    return err[0]?.message ?? fallback;
  }

  return (
    err?.data?.[0]?.message ??
    err?.errors?.[0]?.message ??
    err?.message ??
    err?.title ??
    fallback
  );
}

const RENT_LABEL_PATTERN = /^rent(?:\s*[-\u2013]|\s+upfront\b|$)/i;

function isRentFailedPayment(
  failedPayment: ConsumerFailedPaymentModel,
): boolean {
  if (failedPayment.isRentCharge !== undefined) {
    return failedPayment.isRentCharge;
  }

  return RENT_LABEL_PATTERN.test(failedPayment.label?.trim() ?? '');
}

function hasRentFailedPaymentForBalances(
  failedPayments: ConsumerFailedPaymentModel[],
  balanceUniqueIds: string[],
): boolean {
  return failedPayments.some(
    (failedPayment) =>
      isRentFailedPayment(failedPayment) &&
      getBalanceUniqueIds(failedPayment).some((balanceUniqueId) =>
        balanceUniqueIds.includes(balanceUniqueId),
      ),
  );
}

export function isDisallowedRentCreditCardPayment({
  failedPayments,
  balanceUniqueIds,
  paymentMethod,
}: {
  failedPayments: ConsumerFailedPaymentModel[];
  balanceUniqueIds: string[];
  paymentMethod?:
    | Pick<PaymentAccountResourceModel, 'paymentMethod'>
    | ConsumerPaymentMethodSummaryModel
    | null;
}): boolean {
  return (
    paymentMethod?.paymentMethod === 'CreditCard' &&
    hasRentFailedPaymentForBalances(failedPayments, balanceUniqueIds)
  );
}

export function getBalanceUniqueIds(
  failedPayment: ConsumerFailedPaymentModel,
): string[] {
  return (
    failedPayment.balanceUniqueIds?.length
      ? failedPayment.balanceUniqueIds
      : [failedPayment.balanceUniqueId]
  ).filter(Boolean);
}

export function getRemainingFailedPayments(
  failedPayments: ConsumerFailedPaymentModel[],
  resolvedBalanceUniqueIds: string[],
): ConsumerFailedPaymentModel[] {
  const resolvedBalanceUniqueIdSet = new Set(resolvedBalanceUniqueIds);

  return failedPayments
    .map((failedPayment) => {
      const balanceUniqueIds = getBalanceUniqueIds(failedPayment);
      const remainingBalanceUniqueIds = balanceUniqueIds.filter(
        (balanceUniqueId) => !resolvedBalanceUniqueIdSet.has(balanceUniqueId),
      );

      if (remainingBalanceUniqueIds.length === 0) return null;
      if (remainingBalanceUniqueIds.length === balanceUniqueIds.length) {
        return failedPayment;
      }

      const amountPerBalance =
        failedPayment.amount / Math.max(balanceUniqueIds.length, 1);

      return {
        ...failedPayment,
        balanceUniqueId:
          remainingBalanceUniqueIds[0] ?? failedPayment.balanceUniqueId,
        balanceUniqueIds: remainingBalanceUniqueIds,
        amount: amountPerBalance * remainingBalanceUniqueIds.length,
      };
    })
    .filter(
      (failedPayment): failedPayment is ConsumerFailedPaymentModel =>
        failedPayment != null,
    );
}

export type FailedPaymentsHomeGroup = {
  key: string;
  homeAddress: string | null;
  failedPayments: ConsumerFailedPaymentModel[];
};

export function groupFailedPaymentsByHome(
  failedPayments: ConsumerFailedPaymentModel[],
): FailedPaymentsHomeGroup[] {
  const groups = new Map<string, FailedPaymentsHomeGroup>();

  for (const failedPayment of failedPayments) {
    const key =
      failedPayment.homeUniqueId ?? failedPayment.homeAddress ?? 'unknown-home';
    const group = groups.get(key);

    if (group) {
      group.failedPayments.push(failedPayment);
      group.homeAddress ??= failedPayment.homeAddress ?? null;
    } else {
      groups.set(key, {
        key,
        homeAddress: failedPayment.homeAddress ?? null,
        failedPayments: [failedPayment],
      });
    }
  }

  return Array.from(groups.values());
}

export function isFutureDueDate(value?: string | null) {
  if (!value) return false;
  const dueDate = parseISO(value);
  if (Number.isNaN(dueDate.getTime())) return false;
  return isAfter(startOfDay(dueDate), startOfDay(new Date()));
}

export function createIdempotencyHeaders(
  scope: string,
): Record<string, string> {
  const randomId =
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return {
    'Idempotency-Key': `${scope}-${randomId}`,
  };
}
